/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* Keep Omnisend's mobile teaser visually aligned with the native Shopify Chat
 * launcher. Omnisend injects a campaign-specific class/id after the theme has
 * loaded, so the stable suffixes are detected instead of hard-coding a form ID. */
(function () {
  var MOBILE_QUERY = window.matchMedia('(max-width: 740px)');
  var TARGET_SIZE = 46;
  var DESKTOP_TARGET_SIZE = 100;
  var WIDGET_GAP = 12;
  var scheduled = false;

  function getChatMetrics() {
    var chat = document.querySelector('shopify-chat');
    var activator = chat && chat.shadowRoot && chat.shadowRoot.querySelector('.activator');
    if (!activator) return null;

    var rect = activator.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    return {
      size: Math.max(rect.width, rect.height),
      right: Math.max(0, window.innerWidth - rect.right),
      bottom: Math.max(0, window.innerHeight - rect.top + WIDGET_GAP),
      edgeRight: rect.right,
      targetTeaserBottom: rect.top - WIDGET_GAP
    };
  }

  function findTeaser(button, container) {
    var node = button;

    while (node && node !== container) {
      if (node.classList) {
        var hasTeaserClass = Array.prototype.some.call(node.classList, function (className) {
          return /^omnisend-form-.+-teaser$/.test(className);
        });

        if (hasTeaserClass) return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  function alignOmnisendTeasers() {
    scheduled = false;

    if (!MOBILE_QUERY.matches) {
      document.querySelectorAll('.inyouths-omnisend-teaser').forEach(function (teaser) {
        teaser.classList.remove('inyouths-omnisend-teaser');
        teaser.style.removeProperty('--inyouths-omnisend-scale');
        teaser.style.removeProperty('--inyouths-omnisend-right');
        teaser.style.removeProperty('--inyouths-omnisend-bottom');
      });

      var desktopContainer = document.getElementById('omnisend-forms-container');
      if (!desktopContainer) return;

      desktopContainer.querySelectorAll('[id^="omnisend-form-"][id$="-teaser-btn"]').forEach(function (button) {
        var teaser = findTeaser(button, desktopContainer);
        if (!teaser) return;

        if (!teaser.dataset.inyouthsOmnisendBaseSize) {
          var rect = button.getBoundingClientRect();
          var baseSize = Math.max(rect.width, rect.height);
          if (!baseSize) return;
          teaser.dataset.inyouthsOmnisendBaseSize = String(baseSize);
        }

        var size = Number(teaser.dataset.inyouthsOmnisendBaseSize);
        if (!Number.isFinite(size) || size <= 0) return;
        teaser.style.setProperty('--inyouths-omnisend-desktop-scale', String(Math.min(1, DESKTOP_TARGET_SIZE / size)));
        teaser.classList.add('inyouths-omnisend-teaser-desktop');
      });
      return;
    }

    var container = document.getElementById('omnisend-forms-container');
    if (!container) return;
    var chatMetrics = getChatMetrics();

    container.querySelectorAll('[id^="omnisend-form-"][id$="-teaser-btn"]').forEach(function (button) {
      var teaser = findTeaser(button, container);
      if (!teaser) return;

      teaser.classList.remove('inyouths-omnisend-teaser-desktop');
      teaser.style.removeProperty('--inyouths-omnisend-desktop-scale');

      if (!teaser.dataset.inyouthsOmnisendBaseSize) {
        var rect = button.getBoundingClientRect();
        var baseSize = Math.max(rect.width, rect.height);
        if (!baseSize) return;
        teaser.dataset.inyouthsOmnisendBaseSize = String(baseSize);
      }

      var size = Number(teaser.dataset.inyouthsOmnisendBaseSize);
      if (!Number.isFinite(size) || size <= 0) return;

      var targetSize = chatMetrics ? chatMetrics.size : TARGET_SIZE;
      var teaserRight = chatMetrics ? chatMetrics.right : 20;
      var teaserBottom = chatMetrics ? chatMetrics.bottom : 78;
      teaser.style.setProperty('--inyouths-omnisend-scale', String(Math.min(1, targetSize / size)));
      teaser.style.setProperty('--inyouths-omnisend-right', teaserRight + 'px');
      teaser.style.setProperty('--inyouths-omnisend-bottom', teaserBottom + 'px');
      teaser.classList.add('inyouths-omnisend-teaser');

      /* Omnisend's visible round button is not always centered inside its
       * campaign wrapper. Align the button itself with Shopify Chat instead
       * of trusting the wrapper box, which otherwise makes it look crooked. */
      if (chatMetrics) {
        var buttonRect = button.getBoundingClientRect();
        var visibleRightDistance = window.innerWidth - buttonRect.right;
        teaserRight += chatMetrics.right - visibleRightDistance;
        teaserBottom += buttonRect.bottom - chatMetrics.targetTeaserBottom;
        teaser.style.setProperty('--inyouths-omnisend-right', Math.max(0, teaserRight) + 'px');
        teaser.style.setProperty('--inyouths-omnisend-bottom', Math.max(0, teaserBottom) + 'px');
      }
    });
  }

  function scheduleAlignment() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(alignOmnisendTeasers);
  }

  function start() {
    scheduleAlignment();
    new MutationObserver(scheduleAlignment).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    window.addEventListener('resize', scheduleAlignment, { passive: true });
    if (MOBILE_QUERY.addEventListener) MOBILE_QUERY.addEventListener('change', scheduleAlignment);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
